import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTemplates } from '../../context/TemplatesContext';
import {
  getBuildVersion,
  getRunningMainBundle,
  isStaleBundle,
  formatVersionStamp,
  ROUTE_VERSION_CHECK_EVENT,
} from '../../services/versionApi';
import './Footer.css';

// 換包偵測頻率：Zeabur 換包約 15 分鐘，5 分鐘查一次足夠，切回分頁時另外立刻查
export const VERSION_POLL_MS = 5 * 60 * 1000;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { templates } = useTemplates();
  const companyName = templates?.companyName || '大力膠';

  // BIGLI-67（PM Bridget 2026-09-16 22:16「再次出現舊畫面」）：肉眼可見的版本印記。
  // 「我看到的是不是最新的」一直只能靠 AI 打 /api/version 比指紋（BIGLI-11／BIGLI-20／BIGLI-63
  // 三次都是同一個問題：Zeabur 回 success 但流量還在舊包）。把 sourceFingerprint.short 前 6 碼
  // 印在版權列旁，PM 對照評論裡回報的版本就知道有沒有換包，不用等人查。
  // 拿不到（未部署 build／API 掛了）就不顯示，版權列本身不受影響。
  //
  // BIGLI-67（PM Bridget 2026-10-01「為何此情況會一再出現」）：上面的印記讀的是「伺服器現在的版本」，
  // 但分頁在換包前就開著的話，瀏覽器跑的仍是舊 JS（SPA 換頁不會重抓 index.html），
  // 印記顯示新版、畫面卻是舊的。所以定時＋切回分頁時再查一次，伺服器那包和自己這包不同就
  // 跳出「系統已更新，請重新整理」。不自動重整——編輯中的報價單會丟資料。
  const [version, setVersion] = useState(null);
  const [stale, setStale] = useState(false);
  useEffect(() => {
    let cancelled = false;
    const runningBundle = getRunningMainBundle();
    const check = () => {
      getBuildVersion().then((v) => {
        if (cancelled || !v) return;
        // 印記維持「這個分頁一開始拿到的版本」；換包後由提示條告知，不偷換印記
        setVersion((prev) => prev || v);
        if (isStaleBundle(runningBundle, v.mainBundle)) setStale(true);
      });
    };
    check();
    const timer = setInterval(check, VERSION_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    // BIGLI-92：SPA 換頁（App 的 RouteVersionPing）也立刻查一次
    window.addEventListener(ROUTE_VERSION_CHECK_EVENT, check);
    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(ROUTE_VERSION_CHECK_EVENT, check);
    };
  }, []);
  const versionStamp = formatVersionStamp(version);

  return (
    <footer className="footer">
      <div className="footer-container">
        <p className="copyright">
          © {currentYear} {companyName}. 版權所有.
        </p>
        {versionStamp && (
          <p
            className="version-stamp"
            data-testid="version-stamp"
            title={`原始碼指紋 ${version.fingerprintShort}${version.builtAt ? `／build ${version.builtAt}` : ''}`}
          >
            版本 {versionStamp}
          </p>
        )}
      </div>
      {/* 掛到 body：側欄在手機版有 transform，fixed 放在裡面會跟著側欄跑 */}
      {stale && createPortal(
        <div className="version-update-bar" role="alert" data-testid="version-update-bar">
          <span>系統已更新，目前畫面是舊版本。請先儲存手上的編輯，再重新整理。</span>
          <button type="button" onClick={() => window.location.reload()}>重新整理</button>
        </div>,
        document.body
      )}
    </footer>
  );
};

export default Footer;
